import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Gift } from 'lucide-react';
import { Button } from '../components/ui';
import PaymentService from '../services/paymentService';

const DonationSuccessPage: React.FC = () => {
  const [paymentStatus, setPaymentStatus] = useState<{
    loading: boolean;
    verified: boolean;
    paymentId: string | null;
    amount?: number | undefined;
    eventName?: string | undefined;
    status?: string | undefined;
    statusMessage?: string | undefined;
  }>({
    loading: true,
    verified: false,
    paymentId: null
  });

  useEffect(() => {
    const verifyPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment') || urlParams.get('id');
      const eventName = urlParams.get('event');

      if (paymentId) {
        try {
          const status = await PaymentService.getPaymentStatus(paymentId);
          
          // Map Mollie statuses to user-friendly messages
          const getStatusMessage = (mollieStatus: string) => {
            switch (mollieStatus) {
              case 'paid': return 'Betaling succesvol voltooid';
              case 'open': return 'Betaling nog niet voltooid';
              case 'pending': return 'Betaling wordt verwerkt';
              case 'failed': return 'Betaling mislukt';
              case 'canceled': return 'Betaling geannuleerd';
              case 'cancelled': return 'Betaling geannuleerd';
              case 'expired': return 'Betaling verlopen';
              default: return `Betaling status: ${mollieStatus}`;
            }
          };

          // Since apiGet extracts data automatically, status is the payment data directly
          setPaymentStatus({
            loading: false,
            verified: status?.isPaid || false,
            paymentId,
            amount: status ? parseFloat(status.amount.value) : undefined,
            eventName: eventName || undefined,
            status: status?.status,
            statusMessage: status ? getStatusMessage(status.status) : 'Onbekende status'
          });
        } catch (error) {
          console.error('Error verifying donation payment:', error);
          setPaymentStatus({
            loading: false,
            verified: false,
            paymentId,
            status: 'error',
            statusMessage: 'Kon betalingsstatus niet ophalen'
          });
        }
      } else {
        setPaymentStatus({
          loading: false,
          verified: false,
          paymentId: null,
          status: 'no_payment_id',
          statusMessage: 'Geen betalings-ID gevonden'
        });
      }
    };

    verifyPayment();
  }, []);

  if (paymentStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-neon-cyan mx-auto mb-4"></div>
          <p className="text-lg text-fg-muted">iDEAL donatiestatus controleren...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-2.5">
          <span className="sf-logo-mark h-[34px] w-[34px] text-[19px]">S</span>
          <span className="text-xl font-extrabold tracking-tight text-fg-bright">SOLOFIX</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 pb-16">
        <div className="sf-card overflow-hidden">
          {paymentStatus.verified ? (
            /* SUCCESS - Payment completed */
            <>
              {/* Success Content */}
              <div className="px-8 pt-12 pb-8 text-center">
                <div
                  className="w-24 h-24 mx-auto mb-6 rounded-full bg-neon-lime/[.12] flex items-center justify-center"
                  style={{ boxShadow: '0 0 40px rgba(184,255,61,.35)' }}
                >
                  <CheckCircle className="w-12 h-12 text-neon-lime" />
                </div>
                <div className="sf-eyebrow mb-3">// dankjewel</div>
                <h2 className="text-3xl font-bold text-fg-bright tracking-tight mb-4">
                  Dankjewel voor je Donatie! 💝
                </h2>
                {paymentStatus.eventName ? (
                  <p className="text-lg text-fg-body mb-2">
                    Je hebt succesvol gedoneerd voor <strong className="text-fg-bright">{paymentStatus.eventName}</strong>
                  </p>
                ) : null}
                <p className="text-lg text-fg-muted">
                  Je steun helpt ons SoloFix gratis te houden voor iedereen
                </p>
              </div>

              <div className="px-8 py-8 text-center">
                <div className="mb-8">
                  <div className="flex justify-center mb-4">
                    <Gift className="w-16 h-16 text-neon-pink" />
                  </div>
                  <h3 className="text-2xl font-bold text-fg-bright mb-4">
                    Je donatie is ontvangen!
                  </h3>
                  <p className="text-lg text-fg-body mb-6">
                    Met jouw bijdrage kunnen wij SoloFix blijven verbeteren en gratis houden.
                    Je hebt festival-goers geholpen hun perfecte maatjes te vinden!
                  </p>
                </div>

                {paymentStatus.amount && (
                  <div className="sf-card-highlight p-6 mb-8">
                    <p className="sf-label-mono mb-1">Gedoneerd bedrag</p>
                    <p className="text-4xl font-bold text-fg-bright">
                      €{paymentStatus.amount.toFixed(2)}
                    </p>
                  </div>
                )}

                <div className="sf-item p-6 mb-8 text-left">
                  <h4 className="font-semibold text-fg-bright mb-3">Wat je donatie mogelijk maakt:</h4>
                  <ul className="space-y-2 text-fg-body">
                    <li className="flex items-start">
                      <span className="text-neon-lime font-bold mr-2">✓</span>
                      <span>Server kosten om SoloFix online te houden</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-neon-lime font-bold mr-2">✓</span>
                      <span>Nieuwe features en verbeteringen</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-neon-lime font-bold mr-2">✓</span>
                      <span>Gratis toegang voor alle festival-goers</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-neon-lime font-bold mr-2">✓</span>
                      <span>Ondersteuning voor nieuwe festivals</span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <Button
                    onClick={() => window.location.href = '/'}
                    className="w-full py-3 text-lg"
                  >
                    Terug naar SoloFix
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => window.location.href = '/groups'}
                      className="flex-1 sf-link py-2"
                    >
                      Mijn Groepen
                    </button>
                    <button
                      onClick={() => window.location.href = '/events'}
                      className="flex-1 sf-link py-2"
                    >
                      Festival Agenda
                    </button>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/[.08]">
                  <p className="text-sm text-fg-muted">
                    💌 Nogmaals bedankt voor je steun! Je maakt het verschil voor de community.
                  </p>
                </div>
              </div>
            </>
          ) : (
            /* FAILED/OTHER STATUS */
            <>
              <div className="px-8 pt-12 pb-8 text-center">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-full border flex items-center justify-center ${
                  paymentStatus.status === 'open' || paymentStatus.status === 'pending'
                    ? 'bg-warning-500/10 border-warning-500/30'
                    : 'bg-neon-pink/[.08] border-neon-pink/30'
                }`}>
                  <span className="text-4xl">
                    {paymentStatus.status === 'open' || paymentStatus.status === 'pending' ? '⏳' : '⚠️'}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-fg-bright tracking-tight mb-4">
                  {paymentStatus.status === 'open' ? 'Betaling Open' :
                   paymentStatus.status === 'pending' ? 'Betaling Verwerken' :
                   paymentStatus.status === 'canceled' || paymentStatus.status === 'cancelled' ? 'Betaling Geannuleerd' :
                   paymentStatus.status === 'expired' ? 'Betaling Verlopen' :
                   'Donatie Mislukt'}
                </h2>
                <p className={`text-lg ${
                  paymentStatus.status === 'open' || paymentStatus.status === 'pending'
                    ? 'text-warning-400'
                    : 'text-neon-pink-soft'
                }`}>
                  {paymentStatus.statusMessage || 'Er is iets mis gegaan met je donatie'}
                </p>
              </div>

              <div className="px-8 py-8 text-center">
                {paymentStatus.status === 'open' || paymentStatus.status === 'pending' ? (
                  <>
                    <p className="text-fg-body mb-6">
                      Je betaling is nog niet voltooid. Dit kan enkele minuten duren.
                      Je kunt deze pagina vernieuwen om de status te controleren.
                    </p>
                    <div className="space-y-4">
                      <Button
                        onClick={() => window.location.reload()}
                        className="w-full py-3 text-lg"
                      >
                        Status Vernieuwen
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-fg-body mb-6">
                      {paymentStatus.status === 'canceled' || paymentStatus.status === 'cancelled' ?
                        'Je hebt de betaling geannuleerd. Er is geen bedrag van je rekening afgeschreven.' :
                        paymentStatus.status === 'expired' ?
                        'De betaling is verlopen. Er is geen bedrag van je rekening afgeschreven.' :
                        'Je donatie is niet voltooid. Er is geen bedrag van je rekening afgeschreven.'
                      }
                    </p>

                    {paymentStatus.paymentId && (
                      <div className="sf-item p-4 mb-6">
                        <p className="sf-label-mono mb-2">Betalings-ID:</p>
                        <p className="font-mono text-xs text-fg-soft">{paymentStatus.paymentId}</p>
                        <p className="text-sm text-fg-muted mt-2">Status: {paymentStatus.statusMessage}</p>
                      </div>
                    )}

                    <div className="space-y-4">
                      <Button
                        onClick={() => window.location.href = '/donate'}
                        className="w-full py-3 text-lg"
                      >
                        Probeer Opnieuw
                      </Button>

                      <button
                        onClick={() => window.location.href = '/'}
                        className="sf-link"
                      >
                        Terug naar homepagina
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationSuccessPage;